import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import QRCode from "qrcode";

import { Button } from "@/components/ui/button";
import { REFERRAL } from "@/constants/testIds";

const QR_SIZE = 280;
const CAPTION_HEIGHT = 54;

export const ReferralQr = ({ referralLink, code }) => {
  const canvasRef = useRef(null);
  const [available, setAvailable] = useState(true);
  // The caller supplies the dated share url (`?s=<share id>`), so encode it
  // verbatim rather than appending a source marker of our own.
  const qrLink = referralLink;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") {
      setAvailable(false);
      return;
    }

    try {
      if (!canvas.getContext("2d")) {
        setAvailable(false);
        return;
      }
      Promise.resolve(
        QRCode.toCanvas(canvas, qrLink, {
          width: QR_SIZE,
          margin: 2,
          errorCorrectionLevel: "H",
          color: { dark: "#082421", light: "#f3f1ea" },
        }),
      ).catch(() => setAvailable(false));
    } catch {
      setAvailable(false);
    }
  }, [qrLink]);

  const download = () => {
    const qrCanvas = canvasRef.current;
    const output = document.createElement("canvas");
    output.width = QR_SIZE;
    output.height = QR_SIZE + CAPTION_HEIGHT;

    try {
      const context = output.getContext?.("2d");
      if (!context || !qrCanvas) return;
      context.fillStyle = "#f3f1ea";
      context.fillRect(0, 0, output.width, output.height);
      context.drawImage(qrCanvas, 0, 0, QR_SIZE, QR_SIZE);
      context.fillStyle = "#082421";
      context.font = "700 18px Inter, sans-serif";
      context.textAlign = "center";
      context.fillText(code, output.width / 2, QR_SIZE + 33);

      const anchor = document.createElement("a");
      anchor.download = `${code}.png`;
      anchor.href = output.toDataURL("image/png");
      anchor.click();
    } catch {
      setAvailable(false);
    }
  };

  return (
    <div className="referral-qr">
      <div className="referral-qr-frame">
        <canvas ref={canvasRef} width={QR_SIZE} height={QR_SIZE} aria-label={`QR code for ${code}`} />
        {!available ? <p>QR preview is unavailable on this device.</p> : null}
      </div>
      <strong>{code}</strong>
      <Button
        type="button"
        className="gold-button"
        data-testid={REFERRAL.qrDownloadButton}
        disabled={!available}
        onClick={download}
      >
        <Download /> Download PNG
      </Button>
    </div>
  );
};

export default ReferralQr;
